import { ActionType, createReducer } from 'typesafe-actions'
import { resendTwoFactorCodeAsync, saveAuthUser, signInAsync, signOutAsync, verifyTwoFactorAsync } from './actions'
import { fetchOperatorsHandle, fetchUserPermissionHandle, updateUserHandle, updateUserOnMainApiHandle } from './handles'
import { UserActions, UserState } from './types'

function getPersistedUser() {
  try {
    const stored = localStorage.getItem('devchat_app_userAuthenticated')
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const initialState: UserState = {
  userAuthenticated: getPersistedUser(),
  errorOnSaveAuthUser: null,
  isLoadingSaveAuthUser: false,
  isLoadingSignIn: false,
  isErrorOnSignIn: null,
  isLoadingSignOut: false,
  isErrorOnSignOut: false,
  isLoadingVerifyTwoFactor: false,
  isErrorOnVerifyTwoFactor: null,
  isLoadingResendTwoFactorCode: false,
  isErrorOnResendTwoFactorCode: null,
  users: [],
  isErrorOnLoadingFetchOperators: false,
  isLoadingFetchOperators: false,
  isSuccessOnLoadingFetchOperators: false,
  permission: null,
  isLoadingFetchUserPermission: false,
  isErrorOnLoadingFetchUserPermission: false,
  isSuccessOnLoadingFetchUserPermission: false,
  isErrorOnLoadingUpdateUser: false,
  isLoadingUpdateUser: false,
  isSuccessOnLoadingUpdateUser: false,
  isLoadingUpdateUserOnMainApi: false,
  isErrorOnUpdateUserOnMainApi: false,
  isSuccessOnUpdateUserOnMainApi: false
}

export const userReducer = createReducer<UserState, UserActions>(initialState)
  .handleAction(
    saveAuthUser,
    (state: UserState, action: ActionType<typeof saveAuthUser>): UserState => ({
      ...state,
      isLoadingSaveAuthUser: false,
      userAuthenticated: action.payload
    })
  )
  .handleAction(
    signInAsync.request,
    (state: UserState): UserState => ({
      ...state,
      isLoadingSignIn: true
    })
  )
  .handleAction(
    signInAsync.success,
    (state: UserState, action: ActionType<typeof signInAsync.success>): UserState => ({
      ...state,
      isLoadingSignIn: false,
      userAuthenticated: action.payload.userAuthenticated
    })
  )
  .handleAction(
    signInAsync.failure,
    (state: UserState, action: ActionType<typeof signInAsync.failure>): UserState => ({
      ...state,
      isLoadingSignIn: false,
      isErrorOnSignIn: action.payload
    })
  )
  .handleAction(
    signOutAsync.request,
    (state: UserState): UserState => ({
      ...state,
      isLoadingSignOut: true,
      isErrorOnSignOut: false
    })
  )
  .handleAction(
    signOutAsync.success,
    (state: UserState): UserState => ({
      ...state,
      isLoadingSignOut: false,
      isErrorOnSignOut: false,
      userAuthenticated: null
    })
  )
  .handleAction(
    signOutAsync.failure,
    (state: UserState): UserState => ({
      ...state,
      isLoadingSignOut: false,
      isErrorOnSignOut: true
    })
  )
  .handleAction(
    verifyTwoFactorAsync.request,
    (state: UserState): UserState => ({
      ...state,
      isLoadingVerifyTwoFactor: true,
      isErrorOnVerifyTwoFactor: null
    })
  )
  .handleAction(
    verifyTwoFactorAsync.success,
    (state: UserState): UserState => ({
      ...state,
      isLoadingVerifyTwoFactor: false
    })
  )
  .handleAction(
    verifyTwoFactorAsync.failure,
    (state: UserState, action: ActionType<typeof verifyTwoFactorAsync.failure>): UserState => ({
      ...state,
      isLoadingVerifyTwoFactor: false,
      isErrorOnVerifyTwoFactor: action.payload
    })
  )
  .handleAction(
    resendTwoFactorCodeAsync.request,
    (state: UserState): UserState => ({
      ...state,
      isLoadingResendTwoFactorCode: true,
      isErrorOnResendTwoFactorCode: null
    })
  )
  .handleAction(
    resendTwoFactorCodeAsync.success,
    (state: UserState): UserState => ({
      ...state,
      isLoadingResendTwoFactorCode: false
    })
  )
  .handleAction(
    resendTwoFactorCodeAsync.failure,
    (state: UserState, action: ActionType<typeof resendTwoFactorCodeAsync.failure>): UserState => ({
      ...state,
      isLoadingResendTwoFactorCode: false,
      isErrorOnResendTwoFactorCode: action.payload
    })
  )
  .handleAction(fetchOperatorsHandle.asyncAction.request, fetchOperatorsHandle.request)
  .handleAction(fetchOperatorsHandle.asyncAction.success, fetchOperatorsHandle.success)
  .handleAction(fetchOperatorsHandle.asyncAction.failure, fetchOperatorsHandle.failure)
  .handleAction(fetchUserPermissionHandle.asyncAction.request, fetchUserPermissionHandle.request)
  .handleAction(fetchUserPermissionHandle.asyncAction.success, fetchUserPermissionHandle.success)
  .handleAction(fetchUserPermissionHandle.asyncAction.failure, fetchUserPermissionHandle.failure)
  .handleAction(updateUserHandle.asyncAction.request, updateUserHandle.request)
  .handleAction(updateUserHandle.asyncAction.success, updateUserHandle.success)
  .handleAction(updateUserHandle.asyncAction.failure, updateUserHandle.failure)
  .handleAction(updateUserOnMainApiHandle.asyncAction.request, updateUserOnMainApiHandle.request)
  .handleAction(updateUserOnMainApiHandle.asyncAction.success, updateUserOnMainApiHandle.success)
  .handleAction(updateUserOnMainApiHandle.asyncAction.failure, updateUserOnMainApiHandle.failure)
